import React, { useState, useRef, useEffect, useMemo } from 'react';
import {Spinner, Table, Form, Badge} from 'react-bootstrap';
import { IRateTypes, IProvider } from './types';
import ApiService from 'helpers/apiService';


type Props = {
  data: IRateTypes[];
  providers: IProvider[];
  onUpdate: (updatedRate: IRateTypes) => void;
};

const RatesTable = ({ data, providers, onUpdate }: Props) => {
  const providerNamesById = useMemo(
    () => new Map(providers.map(p => [p.id, p.name] as const)),
    []
  );
  const [updatingId, setUpdatingId] = useState<number | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [tempInterval, setTempInterval] = useState<string>('');
    const editingCellRef = useRef<HTMLTableCellElement | null>(null);
    const tempIntervalRef = useRef<string>(tempInterval);
    tempIntervalRef.current = tempInterval;

// Функция преобразования времени HH:mm:ss в строку с суффиксами
const formatTimeWithSuffix = (timeString: string): string => {
  const [hours, minutes, seconds] = timeString.split(':').map(Number);

  // Конвертируем всё в секунды для анализа
  const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;

  if (hours > 0) {
    return `${hours}h${minutes}m`; // Формат для часов
  }
  if (minutes > 0) {
    return `${minutes}m${seconds}s`; // Формат для минут
  }
  return `${totalSeconds}s`; // Формат для секунд
};

// Обратный парсинг строки с суффиксами в HH:mm:ss
const parseSuffixToTime = (input: string): string => {
  let hours = 0, minutes = 0, seconds = 0;

  // Регулярное выражение для парсинга
  const matches = input.match(/(\d+h)?(\d+m)?(\d+s)?/i);

  if (matches) {
    hours = parseInt(matches[1]) || 0;
    minutes = parseInt(matches[2]) || 0;
    seconds = parseInt(matches[3]) || 0;
  }

  // Форматируем в HH:mm:ss
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0')
  ].join(':');
};

  const handleIntervalChange = async (rate: IRateTypes, timer: string) => {
    setUpdatingId(rate.id);

    try {
      const formattedTime = formatTimeWithSuffix(timer);
      const payload = { pair_id: rate.id, enable_history: rate.enable_history, update_interval: formattedTime };
      await ApiService.updateRate(payload);
      const updated: IRateTypes = { ...rate, update_interval: formattedTime };
      onUpdate(updated);
    } catch (error) {
      console.error('Ошибка обновления интервала:', error);
      onUpdate({ ...rate, update_interval: tempInterval });
    } finally {
      setUpdatingId(null);
      setEditingId(null);
    }
  };

  // Закрытие поля по клику в любом месте и сохранение
  useEffect(() => {
    if (editingId == null) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (editingCellRef.current && !editingCellRef.current.contains(target)) {
        const rate = data.find(r => r.id === editingId);
        if (rate) handleIntervalChange(rate, tempIntervalRef.current);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [editingId, data]);



    const handleCheckboxChange = async (rate: IRateTypes) => {
        setUpdatingId(rate.id);
        const newEnableHistory = !rate.enable_history;

        try {
          const payload = { pair_id: rate.id, enable_history: newEnableHistory, update_interval: rate.update_interval };
          await ApiService.updateRate(payload);
          onUpdate({ ...rate, enable_history: newEnableHistory });
        } catch (error) {
          console.error('Ошибка обновления:', error);
        } finally {
          setUpdatingId(null);
        }
      };



  if (!data?.length) return <p>Нет данных для отображения</p>;

  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th>ID</th>
          <th>Провайдер</th>
          <th>Пара</th>
          <th>Исторические данные</th>
          <th>Интервал обновления</th>
        </tr>
      </thead>
      <tbody>
        {data.map((rate) => (
          <tr key={rate.id}>
            <td>{rate.id}</td>
            <td>{providerNamesById.get(rate.provider_id) ?? rate.provider_id}</td>
            <td>{rate.symbol}</td>
            <td onDoubleClick={() => handleCheckboxChange(rate)} style={{ cursor: 'pointer' }}>
              <Badge bg={rate.enable_history ? 'success' : 'danger'}>
                {rate.enable_history ? 'Активна' : 'Неактивна'}
              </Badge>
            </td>
            <td
              ref={editingId === rate.id ? editingCellRef : undefined}
              onDoubleClick={() => {
                setEditingId(rate.id);
                setTempInterval(parseSuffixToTime(rate.update_interval));
              }}
            >
              {updatingId === rate.id ? (
                <div className="d-flex align-items-center">
                  <Spinner size="sm" />
                </div>
              ) : editingId === rate.id ? (
              <div className="d-flex gap-2">
                <Form.Control
                  type="time"
                  step="1"
                  value={tempInterval}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTempInterval(e.target.value)
                  }
                  onBlur={() => handleIntervalChange(rate, tempInterval)}
                  autoFocus
                />
                <small className="text-muted">
                   Формат: {formatTimeWithSuffix(tempInterval)}
                </small>
              </div>
              ) : (
                rate.update_interval
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default RatesTable;